import { useEffect, useState } from "react";
import { ads, type AdCampaign } from "@/domains/ads";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";

const AdminAds = () => {
  const [items, setItems] = useState<AdCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<string>("pending_review");

  const load = async () => {
    setLoading(true);
    try {
      setItems(await ads.listForAdmin());
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id: string) => {
    try {
      await ads.approve(id);
      toast.success("Campaign approved");
      load();
    } catch (e) { toast.error((e as Error).message); }
  };
  const reject = async (id: string) => {
    const reason = prompt("Reason for rejection?") ?? "";
    if (!reason.trim()) return;
    try {
      await ads.reject(id, reason.trim());
      toast.success("Rejected");
      load();
    } catch (e) { toast.error((e as Error).message); }
  };

  const filtered = items.filter((c) => tab === "all" ? true : c.status === tab);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Ad campaigns</h1>
        <p className="text-sm text-muted-foreground">Review and moderate promoted placements.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending_review">Pending</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No campaigns.</p>
          ) : (
            <div className="space-y-3">
              {filtered.map((c) => (
                <div key={c.id} className="rounded-lg border border-border p-4 bg-card flex gap-4">
                  {c.image_url && (
                    <img src={c.image_url} alt="" className="h-20 w-28 rounded-md object-cover shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{c.title}</p>
                      <Badge variant="outline">{c.status.replace(/_/g, " ")}</Badge>
                      <span className="text-xs text-muted-foreground">{formatMoney(c.amount_cents)}</span>
                    </div>
                    {c.body && <p className="text-sm text-muted-foreground mt-1">{c.body}</p>}
                    {c.cta_url && (
                      <p className="text-xs text-muted-foreground mt-1">→ <a href={c.cta_url} target="_blank" rel="noreferrer" className="underline">{c.cta_url}</a></p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {c.impressions} impressions · {c.clicks} clicks · by {c.advertiser_id.slice(0, 8)}…
                    </p>
                    {c.status === "pending_review" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => approve(c.id)}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(c.id)}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAds;
